import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from '@mui/material';
import {
  NotificationsActiveRounded,
  ErrorOutlineRounded,
  WarningAmberRounded,
  InfoOutlined,
  DoneRounded,
  VisibilityRounded,
  AddAlertRounded,
  DeleteOutlineRounded,
  RefreshRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { errorMessage } from '../lib/api';
import { formatDateTime, relativeTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill, EmptyState, PanelLoader } from '../components/ui/GlassPanel';
import DataTable from '../components/data/DataTable';
import { useToast } from '../components/ui/ToastProvider';

const SEVERITY = {
  critical: { color: palette.danger, icon: <ErrorOutlineRounded sx={{ fontSize: 15 }} />, label: 'Critical' },
  warning: { color: palette.warning, icon: <WarningAmberRounded sx={{ fontSize: 15 }} />, label: 'Warning' },
  info: { color: palette.primary, icon: <InfoOutlined sx={{ fontSize: 15 }} />, label: 'Info' },
};

const SEVERITY_FILTERS = [
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
];

export default function Alerts() {
  const toast = useToast();
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    device_type: '',
    data_field: '',
    operator: '>',
    threshold: '',
    severity: 'warning',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [alertsResponse, rulesResponse] = await Promise.allSettled([
        api.get('/alerts/all', { params: { limit: 300 } }),
        api.get('/alerts/rules'),
      ]);
      if (alertsResponse.status === 'fulfilled') setAlerts(alertsResponse.value?.data || []);
      if (rulesResponse.status === 'fulfilled') setRules(rulesResponse.value?.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to load the alert feed.'));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [load]);

  const counts = useMemo(() => {
    const base = { critical: 0, warning: 0, info: 0, active: 0, acknowledged: 0, resolved: 0 };
    alerts.forEach((alert) => {
      base[alert.severity] = (base[alert.severity] || 0) + 1;
      base[alert.status] = (base[alert.status] || 0) + 1;
    });
    return base;
  }, [alerts]);

  const acknowledge = async (alert) => {
    try {
      await api.post(`/alerts/${alert.id}/acknowledge`);
      toast.success(`Alert on ${alert.device_name} acknowledged.`);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The alert could not be acknowledged.'));
    }
  };

  const resolve = async (alert) => {
    try {
      await api.post(`/alerts/${alert.id}/resolve`);
      toast.success(`Alert on ${alert.device_name} resolved.`);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The alert could not be resolved.'));
    }
  };

  const createRule = async () => {
    if (!ruleForm.name || !ruleForm.data_field || ruleForm.threshold === '') {
      toast.warning('Rule name, data field and threshold are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/alerts/rules', {
        name: ruleForm.name,
        device_type: ruleForm.device_type || null,
        data_field: ruleForm.data_field,
        operator: ruleForm.operator,
        threshold: Number(ruleForm.threshold),
        severity: ruleForm.severity,
      });
      toast.success('Alert rule created.');
      setRuleDialog(false);
      setRuleForm({ name: '', device_type: '', data_field: '', operator: '>', threshold: '', severity: 'warning' });
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The alert rule could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    try {
      await api.delete(`/alerts/rules/${rule.id}`);
      toast.success('Alert rule removed.');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The alert rule could not be removed.'));
    }
  };

  const columns = [
    {
      key: 'created_at',
      label: 'Time',
      width: 150,
      render: (row) => (
        <Tooltip title={formatDateTime(row.created_at)}>
          <Box>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 600 }}>{relativeTime(row.created_at)}</Typography>
            <Typography className="mono" sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
              {formatDateTime(row.created_at)}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'device_name',
      label: 'Device',
      width: 160,
      render: (row) => (
        <Box>
          <Typography className="mono" sx={{ fontSize: '0.76rem', fontWeight: 600 }}>
            {row.device_name}
          </Typography>
          <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>{row.device_type}</Typography>
        </Box>
      ),
    },
    {
      key: 'alert_type',
      label: 'Alert',
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{row.alert_type}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>{row.message}</Typography>
        </Box>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      width: 130,
      render: (row) => {
        const style = SEVERITY[row.severity] || SEVERITY.info;
        return (
          <Chip
            size="small"
            icon={style.icon}
            label={style.label}
            sx={{
              color: style.color,
              background: alpha(style.color, 0.1),
              border: `1px solid ${alpha(style.color, 0.3)}`,
              fontSize: '0.68rem',
              fontWeight: 700,
              '& .MuiChip-icon': { color: style.color, fontSize: 15 },
            }}
          />
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: 140,
      render: (row) => (
        <StatusPill
          label={row.status}
          tone={row.status === 'resolved' ? 'success' : row.status === 'acknowledged' ? 'warning' : 'danger'}
        />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          {row.status === 'active' && (
            <Tooltip title="Acknowledge">
              <IconButton size="small" onClick={() => acknowledge(row)} sx={{ color: palette.warning }}>
                <DoneRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {row.status !== 'resolved' && (
            <Tooltip title="Resolve">
              <IconButton size="small" onClick={() => resolve(row)} sx={{ color: palette.success }}>
                <RefreshRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View device">
            <IconButton size="small" onClick={() => navigate(`/devices/${row.device_name}`)} sx={{ color: palette.primary }}>
              <VisibilityRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* severity summary */}
      <Grid container spacing={2.5}>
        {['critical', 'warning', 'info'].map((key) => {
          const style = SEVERITY[key];
          return (
            <Grid item xs={12} sm={4} key={key}>
              <Box
                sx={{
                  position: 'relative',
                  p: 2.5,
                  borderRadius: 4,
                  border: `1px solid ${alpha(style.color, 0.22)}`,
                  background: `linear-gradient(155deg, ${alpha(style.color, 0.12)}, ${alpha('#070C1C', 0.75)})`,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(120% 90% at 100% 0%, ${alpha(style.color, 0.2)}, transparent 60%)`,
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 2.25,
                      color: style.color,
                      background: alpha(style.color, 0.14),
                      border: `1px solid ${alpha(style.color, 0.32)}`,
                      boxShadow: `inset 0 0 18px ${alpha(style.color, 0.2)}`,
                    }}
                  >
                    {style.icon}
                  </Box>
                  <Box>
                    <Typography className="mono" sx={{ fontSize: '1.5rem', fontWeight: 500, lineHeight: 1 }}>
                      {counts[key] || 0}
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                      {style.label.toUpperCase()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Alert Feed"
          subtitle="Severity-based events across the monitored fleet"
          icon={<NotificationsActiveRounded sx={{ fontSize: 18 }} />}
          action={
            <Button size="small" variant="contained" startIcon={<AddAlertRounded sx={{ fontSize: 15 }} />} onClick={() => setRuleDialog(true)}>
              New rule
            </Button>
          }
        />
      </GlassPanel>

      <GlassPanel>
        {loading ? (
          <PanelLoader label="Loading alert feed" />
        ) : (
          <DataTable
            columns={columns}
            rows={alerts}
            rowKey={(row) => row.id}
            searchKeys={['device_name', 'message', 'alert_type', 'rule_name']}
            searchPlaceholder="Search alerts…"
            filters={[
              { key: 'severity', label: 'Severity', options: SEVERITY_FILTERS },
              { key: 'status', label: 'Status', options: STATUS_FILTERS },
            ]}
            emptyIcon={<NotificationsActiveRounded />}
            emptyTitle="No alerts recorded"
            emptyDescription="All monitored endpoints are operating inside their configured thresholds."
            pageSize={10}
          />
        )}
      </GlassPanel>

      {/* rule management */}
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Alert Rules"
          subtitle="Threshold definitions evaluated against incoming telemetry"
          icon={<AddAlertRounded sx={{ fontSize: 18 }} />}
        />
        <Box sx={{ mt: 2, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {rules.length === 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <EmptyState title="No rules defined" description="Create a rule to start monitoring device parameters." minHeight={160} />
            </Box>
          )}
          {rules.map((rule) => {
            const style = SEVERITY[rule.severity] || SEVERITY.info;
            return (
              <Box
                key={rule.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.75,
                  borderRadius: 3,
                  border: `1px solid ${alpha(style.color, 0.18)}`,
                  background: alpha(style.color, 0.05),
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{rule.name}</Typography>
                  <Typography className="mono" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    {rule.data_field} {rule.operator} {rule.threshold}
                    {rule.device_type ? ` · ${rule.device_type}` : ' · all types'}
                  </Typography>
                </Box>
                <StatusPill label={rule.severity} tone={rule.severity === 'critical' ? 'danger' : rule.severity} pulse={false} />
                <Tooltip title="Delete rule">
                  <IconButton size="small" onClick={() => deleteRule(rule)} sx={{ color: palette.danger }}>
                    <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      </GlassPanel>

      {/* rule dialog */}
      <Dialog open={ruleDialog} onClose={() => setRuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create alert rule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Rule name"
                value={ruleForm.name}
                onChange={(event) => setRuleForm({ ...ruleForm, name: event.target.value })}
                placeholder="Low battery guard"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Data field"
                value={ruleForm.data_field}
                onChange={(event) => setRuleForm({ ...ruleForm, data_field: event.target.value })}
                placeholder="battery"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Operator</InputLabel>
                <Select
                  label="Operator"
                  value={ruleForm.operator}
                  onChange={(event) => setRuleForm({ ...ruleForm, operator: event.target.value })}
                >
                  {['>', '<', '>=', '<=', '==', '!='].map((operator) => (
                    <MenuItem key={operator} value={operator}>
                      {operator}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Threshold"
                value={ruleForm.threshold}
                onChange={(event) => setRuleForm({ ...ruleForm, threshold: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Device type (optional)"
                value={ruleForm.device_type}
                onChange={(event) => setRuleForm({ ...ruleForm, device_type: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select
                  label="Severity"
                  value={ruleForm.severity}
                  onChange={(event) => setRuleForm({ ...ruleForm, severity: event.target.value })}
                >
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRuleDialog(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createRule} disabled={saving}>
            {saving ? 'Saving…' : 'Create rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
